// ============================================================================
// Escalations section — every post-placement mutation on the order (cancellation,
// short supply, return, substitution, …) and the live state of ITS handling.
//
// An escalation is a short procedure whose visible work is the external calls it
// spawns (a Zoho void / credit note / line-cancel / PO re-type / sales return) plus
// any allocation/line it created. Each row here is derived server-side from those
// rows (`oms.sales_order.escalation.handling`) — the same done/pending/failed
// vocabulary as a stage checklist, so a stuck credit note is as visible here as a
// failed invoice is on a leg. A `failed` row is what an operator must act on
// (retry the document in Zoho).
// ============================================================================
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  ExportOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { Button, Collapse, Empty, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useState } from "react";
import CopyText from "./copy_text";
import { fmtText } from "../format";
import { retryEscalation } from "../oms.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import {
  Escalation,
  EscalationHandlingState,
  EscalationItem,
  EscalationStep,
  ExpressUpgradeAllocationOutcome,
  SalesOrderDetail,
} from "../types";

// Per-state presentation — mirrors the stage checklist palette so the two read as one system
// (done→green, in-progress→blue, failed→red, pending/skipped→muted).
const STATE_META: Record<
  EscalationHandlingState,
  { icon: React.ReactNode; color: string; label: string; muted?: boolean }
> = {
  done: { icon: <CheckCircleFilled />, color: "success", label: "done" },
  in_progress: { icon: <LoadingOutlined />, color: "processing", label: "in progress" },
  failed: { icon: <CloseCircleFilled />, color: "error", label: "failed" },
  pending: { icon: <ClockCircleOutlined />, color: "default", label: "pending", muted: true },
  skipped: {
    icon: <MinusCircleOutlined />,
    color: "default",
    label: "not applicable",
    muted: true,
  },
};

// The escalation-level rollup tag shown in each panel header.
const STATUS_META: Record<Escalation["status"], { color: string; label: string }> = {
  done: { color: "success", label: "handled" },
  in_progress: { color: "processing", label: "handling…" },
  failed: { color: "error", label: "needs attention" },
};

// The audit half of an escalation: WHAT the operator chose. `params` carries whatever the triggering
// request supplied for the fields that have no column of their own — the keys differ per escalation
// type, so this renders what is present rather than assuming a shape. Blank values are dropped: a
// null `supplier_id` on a reallocation means "inherit the parent allotment's", which the spawned
// allocation already shows, so printing "supplier —" here would be noise.
const PARAM_LABELS: Record<string, string> = {
  amount: "Charge (incl. GST)",
  charge_amount: "Express charge (incl. GST)",
  reason_code: "Reason",
  // LEGACY keys — no escalation writes these any more (the reduction axes collapsed into the
  // deallocation's `cancel_po` and its reason code), but historic rows still carry them and must
  // still render.
  resolution: "Resolution",
  reduce_supply: "Reduce supply",
  trim_plan: "Shrink this allotment",
  found_at: "Damage found",
  supplier_id: "Supplier",
  supplier_warehouse_id: "Supplier WH",
  supplier_lead_time: "Lead time",
  restock: "Goods coming back",
  return_wh_id: "Return WH",
  target_date: "New delivery date",
  dispatch_date: "New pickup date",
  new_date: "Goods ready on",
  target_delivery_date: "Scheduled for",
  current_wh_id: "Goods now at WH",
  estimate_item_id: "Replacement item",
};

// Keys rendered by a dedicated block below, or already obvious from where the panel sits — never as a
// scalar chip. `items` is the deallocation's per-item table; `allocations` the express upgrade's
// per-allotment verdicts; `allocation_id` repeats the allotment the escalation is already filed under.
const PARAM_KEYS_HANDLED_ELSEWHERE = new Set([
  "items",
  "allocations",
  "allocation_id",
  "changes", // rendered by DateChanges
  "moves", // internal: previous targets, read by the backend
]);

// Fallback labels for escalation types — `type_display` is resolved server-side and always wins;
// this only covers a payload from a backend one release behind the UI, so a new type renders as a
// name instead of its raw snake_case value.
const TYPE_LABELS: Record<string, string> = {
  prepone_delivery: "Prepone delivery",
  reschedule_delivery: "Reschedule delivery",
  add_unloading_charge: "Unloading charge",
  express_upgrade_order: "Express upgrade (order)",
  p2p_upgrade: "P2P upgrade",
};

// The goods an escalation acted on. ONE table for every type — the backend folds a deallocation's
// per-item decisions, a reattempt's picked items and a line-scoped mutation's single line into the
// same row shape, so the section shows the same table whether the escalation cancelled a quantity,
// re-pointed a truck or moved a date. A table rather than a sentence per item: product names run
// long enough that prose wrapped past the fold, and the facts line up column-wise when several items
// move at once. Columns that no row has anything to say about are dropped rather than left empty.
const EscalationItems: React.FC<{ items: EscalationItem[] }> = ({ items }) => {
  if (!items || items.length === 0) return null;
  const hasQty = items.some((i) => i.qty !== null && i.qty !== undefined);
  const hasDestination = items.some((i) => !!i.destination);
  const hasPo = items.some((i) => i.cancel_po !== null && i.cancel_po !== undefined);

  const columns: ColumnsType<EscalationItem> = [
    {
      title: "SO Line",
      dataIndex: "line_id",
      width: 80,
      render: (id: number | null) =>
        id ? <span style={{ whiteSpace: "nowrap" }}>{id}</span> : <span style={{ color: "#bfbfbf" }}>—</span>,
    },
    {
      title: "Item",
      key: "item",
      // Names carry finish, size and edgeband codes — one line with the rest on hover.
      ellipsis: { showTitle: false },
      render: (_, item) =>
        item.product_name ? (
          <Tooltip title={item.product_name}>
            <span>{item.product_name}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
  ];
  if (hasQty) {
    columns.push({
      title: "Qty",
      dataIndex: "qty",
      width: 90,
      render: (qty: string | null, item) =>
        qty ? `${qty}${item.unit ? ` ${item.unit}` : ""}` : "—",
    });
  }
  if (hasDestination) {
    columns.push({
      title: "Goes to",
      dataIndex: "destination",
      width: 220,
      render: (destination: string | null) => destination || <span style={{ color: "#bfbfbf" }}>—</span>,
    });
  }
  if (hasPo) {
    columns.push({
      title: "PO",
      key: "cancel_po",
      width: 110,
      render: (_, item) =>
        item.cancel_po === null || item.cancel_po === undefined ? (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ) : item.cancel_po ? (
          <Tag color="warning" bordered={false}>
            reduced
          </Tag>
        ) : (
          <Tag bordered={false}>left standing</Tag>
        ),
    });
  }

  return (
    <Table
      size="small"
      rowKey={(item, index) => String(item.allocation_item_id ?? `${item.line_id}-${index}`)}
      pagination={false}
      columns={columns}
      dataSource={items}
      style={{ marginBottom: 8 }}
    />
  );
};

// An express upgrade acts on every backorder allotment at once, so its `params.allocations` is a
// table of verdicts, not a scalar — summarised ("2 upgraded, 1 skipped") then listed per allotment.
// A skip is an EXPECTED outcome (e.g. stock: covered by the mode flip), so it renders as plain text
// with its reason, never as an error. Rendered here rather than in `ParamsRow` because a nested
// value handed to React as a child is exactly what blanks a page.
const ExpressUpgradeOutcomes: React.FC<{ outcomes: ExpressUpgradeAllocationOutcome[] }> = ({
  outcomes,
}) => {
  if (outcomes.length === 0) return null;
  const upgraded = outcomes.filter((o) => o.outcome === "upgraded").length;
  const skipped = outcomes.length - upgraded;
  return (
    <div style={{ marginBottom: 8 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", fontWeight: 500 }}>
        {upgraded} upgraded{skipped ? `, ${skipped} skipped` : ""}
      </Typography.Text>
      {outcomes.map((o) => (
        <Typography.Text
          key={o.allocation}
          type="secondary"
          style={{ fontSize: 12, display: "block" }}
        >
          Allocation #{o.allocation}:{" "}
          <span style={{ color: "#262626" }}>
            {o.outcome === "upgraded"
              ? "re-routed to dropship"
              : `skipped${o.reason ? ` — ${fmtText(o.reason)}` : ""}`}
          </span>
        </Typography.Text>
      ))}
    </div>
  );
};

// What a reschedule actually moved: one row per date that changed, per allotment (`params.changes`).
interface DateChange {
  allocation_id: number;
  field: string;
  before: string | null;
  after: string | null;
}

const DateChanges: React.FC<{ changes: DateChange[] }> = ({ changes }) => {
  if (!changes.length) return null;
  const fmt = (v: string | null) => (v ? dayjs(v).format("DD MMM YYYY") : "—");
  const multi = new Set(changes.map((c) => c.allocation_id)).size > 1;
  const columns: ColumnsType<DateChange> = [
    ...(multi
      ? [{ title: "Allocation", dataIndex: "allocation_id", render: (v: number) => `#${v}` }]
      : []),
    { title: "Date", dataIndex: "field" },
    { title: "Before", dataIndex: "before", render: fmt },
    { title: "After", dataIndex: "after", render: (v: string | null) => <strong>{fmt(v)}</strong> },
  ];
  return (
    <Table
      size="small"
      pagination={false}
      rowKey={(c) => `${c.allocation_id}-${c.field}`}
      dataSource={changes}
      columns={columns}
      style={{ marginBottom: 8 }}
      title={() => <span style={{ fontSize: 12, fontWeight: 500 }}>What changed</span>}
    />
  );
};

const fmtParamValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return dayjs(value).format("DD MMM YYYY");
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return dayjs(value).format("DD MMM YYYY, HH:mm");
  }
  return fmtText(value).replace(/_/g, " ");
};

const REFUND_TYPES = new Set(["refund_flow", "damage_refund"]);

const isRefundCancelled = (esc: Escalation) =>
  REFUND_TYPES.has(esc.type) && !!(esc.params as Record<string, unknown> | null)?.cancelled_at;

const paramLabel = (key: string, type: string, params: Record<string, unknown>) => {
  if (REFUND_TYPES.has(type)) {
    if (key === "amount") {
      return params.refund_total_incl_tax ? "Refund (excl. GST)" : "Refund (incl. GST)";
    }
    if (key === "refund_total_incl_tax") return "Refund total (incl. GST)";
    if (key === "cancelled_at") return "Refund cancelled";
  }
  return PARAM_LABELS[key] ?? key.replace(/_/g, " ");
};

const ParamsRow: React.FC<{
  type: string;
  params: Escalation["params"];
  display?: Escalation["params_display"];
}> = ({ type, params, display }) => {
  const entries = Object.entries(params || {}).filter(
    ([k, v]) => v !== null && v !== undefined && v !== "" && !PARAM_KEYS_HANDLED_ELSEWHERE.has(k)
  );
  if (entries.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginBottom: 8 }}>
      {entries.map(([key, value]) => (
        <Typography.Text key={key} type="secondary" style={{ fontSize: 12 }}>
          {paramLabel(key, type, (params || {}) as Record<string, unknown>)}:{" "}
          {/* The resolved name wins where there is one — "Mysore Road WH" reads, "578" doesn't. */}
          <span style={{ color: "#262626" }}>
            {display?.[key] ? String(display[key]) : fmtParamValue(value)}
          </span>
        </Typography.Text>
      ))}
    </div>
  );
};

const StepRow: React.FC<{ step: EscalationStep }> = ({ step }) => {
  const meta = STATE_META[step.state] ?? STATE_META.pending;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 0",
        opacity: meta.muted ? 0.55 : 1,
      }}
    >
      <span style={{ fontSize: 14, display: "inline-flex", width: 16, justifyContent: "center" }}>
        {meta.icon}
      </span>
      <span style={{ minWidth: 200, fontWeight: 500 }}>{step.label}</span>
      <Tag color={meta.color} bordered={false}>
        {meta.label}
      </Tag>
      {step.state === "failed" && step.error ? (
        <span style={{ color: "#cf1322", fontSize: 12 }}>{fmtText(step.error)}</span>
      ) : null}
      {step.zoho_doc_id ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Zoho #{step.zoho_doc_id}
        </Typography.Text>
      ) : null}
      {step.logistics_ref ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Carrier {step.logistics_ref}
        </Typography.Text>
      ) : null}
      {step.at ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(step.at).format("DD MMM YYYY HH:mm")}
        </Typography.Text>
      ) : null}
      {step.allocation_id ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          → allocation #{step.allocation_id}
        </Typography.Text>
      ) : null}
      {step.line_id ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          → line #{step.line_id}
        </Typography.Text>
      ) : null}
    </div>
  );
};

// A first-party retry for a failed escalation: requeues the failed Zoho docs it spawned AND the
// failed carrier ops on the legs it re-planned, in one click. Only shown when the escalation is
// `failed` — a healthy escalation has nothing to retry. `stopPropagation` so the click doesn't also
// toggle the collapse panel it lives in.
const RetryEscalationButton: React.FC<{
  esc: Escalation;
  onDone: (so: SalesOrderDetail) => void;
}> = ({ esc, onDone }) => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  if (esc.status !== "failed") return null;
  // Retrying a failed escalation requeues Zoho docs / carrier bookings.
  if (!hasPermission("oms.retry")) return null;
  return (
    <Button
      size="small"
      danger
      icon={<RedoOutlined />}
      loading={loading}
      onClick={async (e) => {
        e.stopPropagation();
        setLoading(true);
        const so = await retryEscalation(esc.id);
        setLoading(false);
        if (so) onDone(so);
      }}
    >
      Retry
    </Button>
  );
};

// The purchases an escalation acted on, or "Full order" when it acted on the order itself. Kept
// separate from the header so the empty and multi-allotment cases stay legible.
const EscalationScopeTags: React.FC<{ esc: Escalation }> = ({ esc }) => {
  const poNumbers = esc.allocation_po_numbers ?? [];
  if (poNumbers.length === 0) {
    // "Full order" is the backend's claim, not a guess from an empty list: an allotment-level row
    // whose allotment we could not resolve shows nothing rather than claiming the whole order.
    return esc.scope === "order" ? <Tag bordered={false}>Full order</Tag> : null;
  }
  return (
    <>
      {poNumbers.map((po) => (
        <Tag key={po} color="purple" bordered={false} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
          <CopyText value={po} label="PO number">
            PO# {po}
          </CopyText>
        </Tag>
      ))}
    </>
  );
};

const PanelHeader: React.FC<{
  esc: Escalation;
  onDone: (so: SalesOrderDetail) => void;
}> = ({ esc, onDone }) => {
  const meta = isRefundCancelled(esc)
    ? { color: "error", label: "refund cancelled" }
    : STATUS_META[esc.status] ?? STATUS_META.in_progress;
  return (
    <Space wrap size={8}>
      <span style={{ fontWeight: 600 }}>
        {esc.type_display || TYPE_LABELS[esc.type] || esc.type}
      </span>
      <Tag color={meta.color} bordered={false}>
        {meta.label}
      </Tag>
      <RetryEscalationButton esc={esc} onDone={onDone} />
      {/* WHICH purchase the escalation acted on — one tag per allotment, the same purple tag the
          allocation card carries so the two read as one key. A row that names no line and no
          allotment acted on the whole order, and says so instead of showing nothing. */}
      <EscalationScopeTags esc={esc} />
      {esc.qty ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          qty {esc.qty}
        </Typography.Text>
      ) : null}
      {esc.sales_order_line_id ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          line #{esc.sales_order_line_id}
        </Typography.Text>
      ) : null}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {dayjs(esc.created_at).format("DD MMM YYYY HH:mm")}
        {/* Null when nobody raised it by hand — the nightly p2p sweep and other system-raised
            mutations say so explicitly rather than showing a blank where a name belongs. */}
        {esc.created_by ? ` · by ${esc.created_by}` : " · by the system"}
      </Typography.Text>
    </Space>
  );
};

const EscalationPanelBody: React.FC<{ esc: Escalation }> = ({ esc }) => {
  const done = esc.checklist.filter((s) => s.state === "done").length;
  const counted = esc.checklist.filter((s) => s.state !== "skipped").length;
  return (
    <div>
      {esc.reason ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 13 }}>
          {esc.reason}
        </Typography.Paragraph>
      ) : null}
      <ParamsRow type={esc.type} params={esc.params} display={esc.params_display} />
      <EscalationItems items={esc.items} />
      <ExpressUpgradeOutcomes
        outcomes={(esc.params?.allocations as ExpressUpgradeAllocationOutcome[]) ?? []}
      />
      <DateChanges changes={(esc.params?.changes as DateChange[]) ?? []} />
      {esc.checklist.length === 0 ? (
        <span style={{ color: "#8c8c8c", fontSize: 12 }}>
          Completed on trigger — no external calls to track.
        </span>
      ) : (
        <>
          <div style={{ marginBottom: 6, color: "#595959", fontSize: 12 }}>
            Handling · {done}/{counted} done
          </div>
          {esc.checklist.map((step) => (
            <StepRow key={step.key} step={step} />
          ))}
        </>
      )}
    </div>
  );
};

const EscalationsSection: React.FC<{
  escalations: Escalation[];
  onDone: (so: SalesOrderDetail) => void;
}> = ({ escalations, onDone }) => {
  if (!escalations || escalations.length === 0) {
    return <Empty description="No escalations on this order" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  // Default-open anything that still needs eyes (failed or mid-handling); collapse the settled ones.
  const defaultOpen = escalations
    .filter((e) => e.status !== "done")
    .map((e) => String(e.id));
  return (
    <Collapse defaultActiveKey={defaultOpen} style={{ marginBottom: 8 }}>
      {escalations.map((esc) => (
        <Collapse.Panel
          key={String(esc.id)}
          header={<PanelHeader esc={esc} onDone={onDone} />}
        >
          <EscalationPanelBody esc={esc} />
        </Collapse.Panel>
      ))}
    </Collapse>
  );
};

// A compact one-line summary for the header (e.g. "2 escalations · 1 needs attention"), so an order
// with a stuck compensating call flags itself without expanding the section.
export const EscalationsSummaryTag: React.FC<{ escalations: Escalation[] }> = ({ escalations }) => {
  if (!escalations || escalations.length === 0) return null;
  const failed = escalations.filter((e) => e.status === "failed").length;
  const inProgress = escalations.filter((e) => e.status === "in_progress").length;
  const color = failed ? "error" : inProgress ? "processing" : "default";
  const suffix = failed
    ? ` · ${failed} needs attention`
    : inProgress
    ? ` · ${inProgress} handling`
    : "";
  const label = `${escalations.length} escalation${escalations.length > 1 ? "s" : ""}${suffix}`;
  return failed ? (
    <Tooltip title="An escalation has a failed external call — expand Escalations to retry it.">
      <Tag color={color} icon={<ExportOutlined />}>
        {label}
      </Tag>
    </Tooltip>
  ) : (
    <Tag color={color}>{label}</Tag>
  );
};

export default EscalationsSection;
